/**
 * Ocean Booking Detail Page
 *
 * Displays a single ocean booking with its original document.
 * Features:
 * - Split view: Original document (left) + OB data (right)
 * - Edit mode toggle
 * - Status management (Archive, Delete)
 * - Navigation back to ocean bookings list
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePageContext } from '../context/PageContext';
import DocumentViewer from '../components/documents/DocumentViewer';
import OBForm from '../components/ocean-booking/OBForm';
import QueueDetailDrawer from '../components/documents/QueueDetailDrawer';
import Button from '../components/common/Button';
import StatusBadge from '../components/common/StatusBadge';
import SparklesIcon from '../components/common/SparklesIcon';
import { useAuth } from '../context/AuthContext';
import * as oceanBookingApi from '../api/oceanBookingApi';
import { getQueueEntryByOB } from '../api/queueApi';
import * as fileApi from '../api/fileApi';
import { formatDateFull, formatDateOnly } from '../utils/formatDate';
import { getCountryName } from '../constants/countries';

/**
 * Booking status color mappings for StatusBadge
 */
const bookingStatusColors = {
  RECEIVED: 'bg-blue-100 text-blue-800',
  PENDING_UPDATE: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  DECLINED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
  COMPLETED: 'bg-emerald-100 text-emerald-800'
};

/**
 * Format a structured address for display
 * @param {Object} address - Structured address object
 * @returns {string|null} Formatted address string
 */
function formatAddress(address) {
  if (!address) return null;

  // Handle legacy string addresses
  if (typeof address === 'string') return address;

  const parts = [];

  // Build address lines
  const addressLines = [
    address.addressLine1,
    address.addressLine2,
    address.addressLine3
  ].filter(Boolean);
  if (addressLines.length > 0) parts.push(addressLines.join('\n'));

  // City, State PostalCode on one line
  const cityLine = [
    address.city,
    address.state,
    address.postalCode
  ].filter(Boolean).join(', ');
  if (cityLine) parts.push(cityLine);

  // Country name
  if (address.country) {
    parts.push(getCountryName(address.country));
  }

  return parts.length > 0 ? parts.join('\n') : null;
}

/**
 * Build a concise equipment summary string from requested equipment.
 * e.g. "2x 40HC, 1x 20GP"
 */
function getEquipmentSummary(requestedEquipment) {
  if (!requestedEquipment || requestedEquipment.length === 0) return null;
  return requestedEquipment
    .map(eq => `${eq.units || 0}x ${eq.ISOEquipmentCode || '?'}`)
    .join(', ');
}

/**
 * Find the carrier party name from the parties array.
 */
function getCarrierName(parties) {
  if (!parties || parties.length === 0) return null;
  const carrier = parties.find(p => p.type === 'CARRIER');
  return carrier?.name || null;
}

/**
 * Find a location by type from the shipmentLocations array.
 */
function getLocationByType(locations, type) {
  if (!locations || locations.length === 0) return null;
  const loc = locations.find(l => l.locationType === type);
  return loc?.locationName || loc?.UNLocationCode || null;
}

/**
 * OceanBookingDetail Component
 */
function OceanBookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setPageContext, clearPageContext } = usePageContext();

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // State
  const [oceanBooking, setOceanBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Document signed URL state
  const [documentSignedUrl, setDocumentSignedUrl] = useState(null);
  const [documentMimeType, setDocumentMimeType] = useState(null);

  // Queue detail drawer state
  const [queueEntry, setQueueEntry] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  /**
   * Fetch ocean booking data
   */
  useEffect(() => {
    const fetchOceanBooking = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await oceanBookingApi.getOceanBooking(id);
        setOceanBooking(response.data);
      } catch (err) {
        setError(err.message || 'Failed to load ocean booking');
      } finally {
        setLoading(false);
      }
    };

    fetchOceanBooking();
  }, [id]);

  // Fetch associated queue entry
  useEffect(() => {
    if (!oceanBooking) return;
    getQueueEntryByOB(oceanBooking._id)
      .then(response => {
        setQueueEntry(response.data);
        setIsDrawerOpen(true);
      })
      .catch(() => setQueueEntry(null));
  }, [oceanBooking?._id]);

  // Publish page context so the ChatWidget can inform the agent about this booking.
  useEffect(() => {
    if (!oceanBooking) return;

    const parties = (oceanBooking.parties || []).map(p => ({
      type: p.type,
      name: p.name,
    }));

    const pol = getLocationByType(oceanBooking.shipmentLocations, 'POL');
    const pod = getLocationByType(oceanBooking.shipmentLocations, 'POD');

    setPageContext({
      page: 'Ocean Booking Detail',
      bookingRequestNumber: oceanBooking.header?.bookingRequestNumber || '(unknown)',
      bookingId: oceanBooking._id,
      status: oceanBooking.status,
      bookingStatus: oceanBooking.header?.bookingStatus,
      parties,
      equipmentSummary: getEquipmentSummary(oceanBooking.requestedEquipment),
      route: pol && pod ? `${pol} -> ${pod}` : null,
    });

    return () => clearPageContext();
  }, [oceanBooking, setPageContext, clearPageContext]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Build proxy URL for document when booking is loaded (avoids CORS issues)
  useEffect(() => {
    if (!oceanBooking || !oceanBooking.documentObjectKey) return;

    // Use proxy URL through backend to avoid CORS issues
    setDocumentSignedUrl(fileApi.buildProxyUrl(oceanBooking.documentObjectKey));
    // Determine mime type from file extension
    const ext = oceanBooking.documentObjectKey.split('.').pop().toLowerCase();
    const mimeMap = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
      tiff: 'image/tiff',
      tif: 'image/tiff',
      csv: 'text/csv',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      edi: 'text/plain',
      txt: 'text/plain',
    };
    setDocumentMimeType(mimeMap[ext] || 'application/octet-stream');
  }, [oceanBooking]);

  /**
   * Handle form submission (update booking)
   */
  const handleSubmit = async (formData) => {
    setIsSaving(true);
    setError(null);

    try {
      const response = await oceanBookingApi.updateOceanBooking(id, formData);
      setOceanBooking(response.data);
      setIsEditing(false);
      setSuccessMessage('Ocean booking updated successfully!');
    } catch (err) {
      setError(err.message || 'Failed to update ocean booking');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle status change (archive/restore)
   */
  const handleStatusChange = async (newStatus) => {
    setIsUpdatingStatus(true);
    setError(null);

    try {
      const response = await oceanBookingApi.updateStatus(id, newStatus);
      setOceanBooking(response.data);
      setSuccessMessage(`Ocean booking ${newStatus.toLowerCase()} successfully!`);
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this ocean booking? This action cannot be undone.')) {
      return;
    }

    try {
      await oceanBookingApi.deleteOceanBooking(id);
      navigate('/ocean-bookings', {
        state: { message: 'Ocean booking deleted successfully!' }
      });
    } catch (err) {
      setError(err.message || 'Failed to delete ocean booking');
    }
  };

  // Document visibility state
  const [isDocumentVisible, setIsDocumentVisible] = useState(false);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading ocean booking...</p>
        </div>
      </div>
    );
  }

  // Error state (fatal - couldn't load booking)
  if (error && !oceanBooking) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center max-w-md">
          <svg
            className="mx-auto h-16 w-16 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="mt-4 text-xl font-medium text-gray-900">
            Failed to Load Ocean Booking
          </h2>
          <p className="mt-2 text-gray-600">{error}</p>
          <div className="mt-4 space-x-4">
            <button
              onClick={() => window.location.reload()}
              className="text-blue-600 hover:underline"
            >
              Try Again
            </button>
            <span className="text-gray-400">|</span>
            <Link to="/ocean-bookings" className="text-blue-600 hover:underline">
              Back to List
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const hasDocument = !!documentSignedUrl;
  const showDocument = hasDocument && isDocumentVisible;

  // Derived display values
  const carrierName = getCarrierName(oceanBooking?.parties);
  const pol = getLocationByType(oceanBooking?.shipmentLocations, 'POL');
  const pod = getLocationByType(oceanBooking?.shipmentLocations, 'POD');
  const equipmentSummary = getEquipmentSummary(oceanBooking?.requestedEquipment);

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Page Header */}
      <div className="flex-shrink-0 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">
                  {oceanBooking?.header?.bookingRequestNumber || 'Ocean Booking'}
                </h1>
                <StatusBadge status={oceanBooking?.status} variant="po" />
                {oceanBooking?.header?.bookingStatus && (
                  <StatusBadge
                    status={oceanBooking.header.bookingStatus}
                    colors={bookingStatusColors}
                  />
                )}
              </div>
              {/* Stats row: carrier, route, equipment, expected departure */}
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                <span>Created {formatDateFull(oceanBooking?.createdAt)}</span>
                {carrierName && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span>{carrierName}</span>
                  </>
                )}
                {pol && pod && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span>{pol} &rarr; {pod}</span>
                  </>
                )}
                {equipmentSummary && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span>{equipmentSummary}</span>
                  </>
                )}
                {oceanBooking?.header?.expectedDepartureDate && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span>ETD {formatDateOnly(oceanBooking.header.expectedDepartureDate)}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Document Toggle */}
            {hasDocument && (
              <Button
                variant="secondary"
                onClick={() => setIsDocumentVisible(!isDocumentVisible)}
                title={isDocumentVisible ? "Hide Original Document" : "Show Original Document"}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isDocumentVisible ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  )}
                </svg>
              </Button>
            )}

            {/* Queue details toggle */}
            {queueEntry && (
              <button
                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                className={`p-2 rounded-md border transition-colors ${isDrawerOpen
                  ? 'bg-blue-50 border-blue-200 text-blue-600'
                  : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
                title={isDrawerOpen ? 'Hide AI extraction details' : 'AI extraction details'}
              >
                <SparklesIcon className="w-5 h-5" />
              </button>
            )}

            <div className="h-6 w-px bg-gray-300 mx-1"></div>

            {/* Edit toggle */}
            {!isEditing && (
              <Button
                variant="secondary"
                onClick={() => setIsEditing(true)}
                title="Edit Ocean Booking"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </Button>
            )}

            {isEditing && (
              <Button
                variant="secondary"
                onClick={() => setIsEditing(false)}
              >
                Cancel Edit
              </Button>
            )}

            {/* More actions menu */}
            <div className="relative">
              <Button
                variant="secondary"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                title="More actions"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
              </Button>
              {isMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setIsMenuOpen(false)} />
                  <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                    {oceanBooking?.status === 'ACTIVE' && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        disabled={isUpdatingStatus}
                        onClick={() => { setIsMenuOpen(false); handleStatusChange('ARCHIVED'); }}
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Archive
                      </button>
                    )}
                    {oceanBooking?.status === 'ARCHIVED' && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        disabled={isUpdatingStatus}
                        onClick={() => { setIsMenuOpen(false); handleStatusChange('ACTIVE'); }}
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Restore
                      </button>
                    )}
                    {isAdmin && (
                      <>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          onClick={() => { setIsMenuOpen(false); handleDelete(); }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="flex-shrink-0 mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="ml-2 text-sm text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Message (non-fatal) */}
      {error && oceanBooking && (
        <div className="flex-shrink-0 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="ml-2 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Split Pane Container */}
      <div className="flex-1 flex mt-4 min-h-0 gap-2">
        {/* Left Pane - Document Viewer */}
        <div className={`${showDocument ? 'w-[40%] shrink-0' : 'w-0 hidden'} border border-gray-200 rounded-lg overflow-hidden transition-all duration-300`}>
          <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
            <h2 className="text-sm font-medium text-gray-700">
              Original Document
            </h2>
          </div>
          <div className="h-[calc(100%-2.5rem)]">
            <DocumentViewer
              url={documentSignedUrl}
              mimeType={documentMimeType}
              objectKey={oceanBooking.documentObjectKey}
              filename={queueEntry?.files?.[0]?.originalName || oceanBooking.documentObjectKey.split('/').pop()}
              detectedFileType={queueEntry?.files?.[0]?.detectedFileType}
            />
          </div>
        </div>

        {/* Right Pane - OB Data */}
        <div className="flex-1 min-w-0 border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-sm font-medium text-gray-700">
              Ocean Booking Data
            </h2>
            {isEditing && (
              <span className="text-xs text-blue-600 font-medium">Editing</span>
            )}
          </div>
          <div className="h-[calc(100%-2.5rem)] overflow-auto p-4">
            {isEditing ? (
              <OBForm
                initialData={oceanBooking}
                onSubmit={handleSubmit}
                isSubmitting={isSaving}
                submitLabel="Save Changes"
              />
            ) : (
              <OBReadOnlyView oceanBooking={oceanBooking} />
            )}
          </div>
        </div>
      </div>

      {/* Overlay drawer for queue details + extraction pipeline */}
      <QueueDetailDrawer
        open={isDrawerOpen && !!queueEntry}
        queueEntry={queueEntry}
        evaluation={queueEntry?.evaluation}
        confidence={queueEntry?.confidence}
        onClose={() => setIsDrawerOpen(false)}
      />
    </div>
  );
}

/**
 * Read-only view of ocean booking data
 */
function OBReadOnlyView({ oceanBooking }) {
  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Header Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Booking Request Number</label>
            <p className="mt-1 text-sm text-gray-900">{oceanBooking?.header?.bookingRequestNumber || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Booking Status</label>
            <p className="mt-1 text-sm text-gray-900">{oceanBooking?.header?.bookingStatus || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Carrier Booking Reference</label>
            <p className="mt-1 text-sm text-gray-900">{oceanBooking?.header?.carrierBookingReference || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Carrier Code</label>
            <p className="mt-1 text-sm text-gray-900">{oceanBooking?.header?.carrierCode || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Service Contract</label>
            <p className="mt-1 text-sm text-gray-900">{oceanBooking?.header?.serviceContractReference || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Incoterms</label>
            <p className="mt-1 text-sm text-gray-900">{oceanBooking?.header?.incoTerms || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Expected Departure</label>
            <p className="mt-1 text-sm text-gray-900">
              {oceanBooking?.header?.expectedDepartureDate
                ? formatDateOnly(oceanBooking.header.expectedDepartureDate)
                : '-'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Expected Arrival</label>
            <p className="mt-1 text-sm text-gray-900">
              {oceanBooking?.header?.expectedArrivalDate
                ? formatDateOnly(oceanBooking.header.expectedArrivalDate)
                : '-'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Freight Payment</label>
            <p className="mt-1 text-sm text-gray-900">
              {oceanBooking?.header?.freightPaymentTermCode === 'PRE' ? 'Prepaid' :
               oceanBooking?.header?.freightPaymentTermCode === 'COL' ? 'Collect' : '-'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Transport Document Type</label>
            <p className="mt-1 text-sm text-gray-900">
              {oceanBooking?.header?.transportDocumentTypeCode === 'BOL' ? 'Bill of Lading' :
               oceanBooking?.header?.transportDocumentTypeCode === 'SWB' ? 'Sea Waybill' : '-'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Receipt Type at Origin</label>
            <p className="mt-1 text-sm text-gray-900">{oceanBooking?.header?.receiptTypeAtOrigin || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Delivery Type at Destination</label>
            <p className="mt-1 text-sm text-gray-900">{oceanBooking?.header?.deliveryTypeAtDestination || '-'}</p>
          </div>
        </div>
      </div>

      {/* Parties Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Parties</h3>
        {oceanBooking?.parties?.length > 0 ? (
          <div className="flex flex-wrap gap-4">
            {oceanBooking.parties.map((party, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 flex-1 min-w-[300px] border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                    {party.type?.replace(/_/g, ' ')}
                  </span>
                  <span className="font-medium text-gray-900">{party.name}</span>
                </div>
                {formatAddress(party.address) && (
                  <p className="text-sm text-gray-600 whitespace-pre-line">{formatAddress(party.address)}</p>
                )}
                {party.contactName && (
                  <p className="text-sm text-gray-500 mt-2">Contact: {party.contactName}</p>
                )}
                {party.email && (
                  <p className="text-sm text-gray-500">Email: {party.email}</p>
                )}
                {party.phone && (
                  <p className="text-sm text-gray-500">Phone: {party.phone}</p>
                )}
                {party.identifyingCodes?.length > 0 && (
                  <div className="mt-2">
                    {party.identifyingCodes.map((code, cIdx) => (
                      <p key={cIdx} className="text-sm text-gray-500">
                        {code.codeType}: {code.value}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No parties defined</p>
        )}
      </div>

      {/* Shipment Locations Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Shipment Locations</h3>
        {oceanBooking?.shipmentLocations?.length > 0 ? (
          <div className="flex flex-wrap gap-4">
            {oceanBooking.shipmentLocations.map((location, index) => {
              const typeLabels = {
                PLC: 'Place of Receipt',
                POL: 'Port of Loading',
                POD: 'Port of Discharge',
                PLD: 'Place of Delivery'
              };
              return (
                <div key={index} className="bg-gray-50 rounded-lg p-4 flex-1 min-w-[250px] border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-800">
                      {typeLabels[location.locationType] || location.locationType}
                    </span>
                  </div>
                  <p className="font-medium text-gray-900">{location.locationName || '-'}</p>
                  {location.UNLocationCode && (
                    <p className="text-sm text-gray-500">UN/LOCODE: {location.UNLocationCode}</p>
                  )}
                  {location.facilityCode && (
                    <p className="text-sm text-gray-500">Facility: {location.facilityCode}</p>
                  )}
                  {formatAddress(location.address) && (
                    <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">{formatAddress(location.address)}</p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No shipment locations defined</p>
        )}
      </div>

      {/* Requested Equipment Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Requested Equipment</h3>
        {oceanBooking?.requestedEquipment?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Equipment</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Units</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Shipper Owned</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Commodities</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {oceanBooking.requestedEquipment.map((eq, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-sm text-gray-900 font-medium">
                      {eq.ISOEquipmentCode || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-right">{eq.units || 0}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-center">
                      {eq.isShipperOwned ? 'Yes' : 'No'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {eq.commodities?.length > 0
                        ? eq.commodities.map(c => c.description || c.commodityType || '-').join(', ')
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No equipment requested</p>
        )}
      </div>

      {/* Transport Plan Section (from carrier confirmation) */}
      {oceanBooking?.transportPlan?.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Transport Plan</h3>
          <div className="space-y-3">
            {oceanBooking.transportPlan.map((stage, index) => {
              const stageLabels = {
                PRC: 'Pre-carriage',
                MNC: 'Main Carriage',
                ONC: 'On-carriage'
              };
              return (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 text-xs font-medium rounded bg-teal-100 text-teal-800">
                      {stageLabels[stage.stageType] || stage.stageType || 'Leg ' + (index + 1)}
                    </span>
                    {stage.vesselName && (
                      <span className="font-medium text-gray-900">{stage.vesselName}</span>
                    )}
                    {stage.voyageNumber && (
                      <span className="text-sm text-gray-500">/ {stage.voyageNumber}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {stage.loadLocation && (
                      <div>
                        <span className="text-gray-500">From: </span>
                        <span className="text-gray-900">{stage.loadLocation}</span>
                      </div>
                    )}
                    {stage.dischargeLocation && (
                      <div>
                        <span className="text-gray-500">To: </span>
                        <span className="text-gray-900">{stage.dischargeLocation}</span>
                      </div>
                    )}
                    {stage.estimatedDeparture && (
                      <div>
                        <span className="text-gray-500">ETD: </span>
                        <span className="text-gray-900">{formatDateOnly(stage.estimatedDeparture)}</span>
                      </div>
                    )}
                    {stage.estimatedArrival && (
                      <div>
                        <span className="text-gray-500">ETA: </span>
                        <span className="text-gray-900">{formatDateOnly(stage.estimatedArrival)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Charges Section (from carrier confirmation) */}
      {oceanBooking?.charges?.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Charges</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Charge</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Basis</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {oceanBooking.charges.map((charge, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-sm text-gray-900">{charge.chargeName || '-'}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-right">
                      {charge.currencyAmount != null ? charge.currencyAmount.toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">{charge.currencyCode || '-'}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {charge.paymentTermCode === 'PRE' ? 'Prepaid' :
                       charge.paymentTermCode === 'COL' ? 'Collect' : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">{charge.calculationBasis || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* References Section */}
      {oceanBooking?.references?.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">References</h3>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              {oceanBooking.references.map((ref, index) => (
                <div key={index}>
                  <label className="block text-sm font-medium text-gray-500">{ref.type || 'Reference'}</label>
                  <p className="mt-1 text-sm text-gray-900">{ref.value || '-'}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cut-off Times (from carrier confirmation) */}
      {oceanBooking?.shipmentCutOffTimes?.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Cut-off Times</h3>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              {oceanBooking.shipmentCutOffTimes.map((cutOff, index) => (
                <div key={index}>
                  <label className="block text-sm font-medium text-gray-500">
                    {cutOff.cutOffDateTimeCode || 'Cut-off'}
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {cutOff.cutOffDateTime ? formatDateFull(cutOff.cutOffDateTime) : '-'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Feedbacks (from carrier confirmation) */}
      {oceanBooking?.feedbacks?.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Carrier Feedback</h3>
          <div className="space-y-2">
            {oceanBooking.feedbacks.map((fb, index) => {
              const severityColors = {
                INFO: 'bg-blue-50 border-blue-200 text-blue-800',
                WARNING: 'bg-yellow-50 border-yellow-200 text-yellow-800',
                ERROR: 'bg-red-50 border-red-200 text-red-800'
              };
              return (
                <div
                  key={index}
                  className={`rounded-lg p-3 border ${severityColors[fb.severity] || severityColors.INFO}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium uppercase">{fb.severity}</span>
                    {fb.code && <span className="text-xs text-gray-500">({fb.code})</span>}
                  </div>
                  <p className="text-sm mt-1">{fb.message || '-'}</p>
                  {fb.property && (
                    <p className="text-xs text-gray-500 mt-1">Property: {fb.property}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Extraction Confidence */}
      {oceanBooking?.extractionConfidence && Object.keys(oceanBooking.extractionConfidence).length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">AI Extraction Confidence</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(oceanBooking.extractionConfidence).map(([field, value]) => {
                // Handle array values (per-field format for parties/equipment)
                if (Array.isArray(value)) {
                  const allScores = value.flatMap(entry =>
                    entry && typeof entry === 'object' ? Object.values(entry).filter(v => typeof v === 'number') : []
                  );
                  if (allScores.length === 0) return null;
                  const avg = allScores.reduce((a, b) => a + b, 0) / allScores.length;
                  return (
                    <div key={field} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{field} (avg)</span>
                      <span className={`text-sm font-medium ${avg >= 0.8 ? 'text-green-600' : 'text-yellow-600'}`}>
                        {Math.round(avg * 100)}%
                      </span>
                    </div>
                  );
                }
                // Scalar value
                if (typeof value !== 'number') return null;
                return (
                  <div key={field} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{field}</span>
                    <span className={`text-sm font-medium ${value >= 0.8 ? 'text-green-600' : 'text-yellow-600'}`}>
                      {Math.round(value * 100)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OceanBookingDetail;
